import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert, Badge, Spinner, Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { BookOpen, GraduationCap, ShieldCheck, UserPlus, ArrowLeft, Mail, KeyRound } from 'lucide-react';
import api from '../api/api';
import { setUser, setBranding } from '../redux/slices/userSlice';

// mode: 'login' | 'forgot' | 'sent' | 'reset' | 'reset-done'
const ClassroomLoginPage = () => {
    const [searchParams]                      = useSearchParams();
    const [slug, setSlug]                     = useState(searchParams.get('slug') || '');
    const [email, setEmail]                   = useState('');
    const [password, setPassword]             = useState('');
    const [acceptedTerms, setAcceptedTerms]   = useState(false);
    const [loading, setLoading]               = useState(false);
    const [error, setError]                   = useState('');
    const [mode, setMode]                     = useState('login');

    // Forgot-password fields
    const [forgotEmail, setForgotEmail]       = useState('');

    // Reset-password fields (from URL token)
    const [resetToken,     setResetToken]     = useState('');
    const [newPassword,    setNewPassword]    = useState('');
    const [confirmPassword,setConfirmPassword]= useState('');

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { branding } = useSelector((state) => state.user);

    // Detect reset token in URL params
    useEffect(() => {
        const token    = searchParams.get('reset_token');
        const slugParam= searchParams.get('slug');
        if (token) {
            setResetToken(token);
            if (slugParam) setSlug(slugParam);
            setMode('reset');
        }
    }, [searchParams]);

    // Fetch branding when slug changes
    useEffect(() => {
        if (slug.length >= 3) {
            api.get(`/public/tenant-branding/${slug}`)
               .then(res => dispatch(setBranding({ name: res.data.name, logo: res.data.logo })))
               .catch(() => {});
        }
    }, [slug, dispatch]);

    // ── Login ─────────────────────────────────────────────────────────────────
    const handleLogin = async (e) => {
        e.preventDefault();
        if (!acceptedTerms) { setError('You must accept the Terms of Service and Privacy Policy to continue.'); return; }
        setLoading(true); setError('');
        try {
            const res = await api.post(
                `/tenant/${slug}/classroom/token?terms_accepted=true`,
                new URLSearchParams({ username: email, password })
            );
            localStorage.setItem('access_token', res.data.access_token);
            dispatch(setUser({ email, tenant_slug: slug, app_context: 'classroom' }));
            navigate(`/tenant/${slug}/copilot`);
        } catch (err) {
            setError(err.response?.data?.detail || 'Verification failed. Check your slug and credentials.');
        } finally { setLoading(false); }
    };

    // ── Forgot password ───────────────────────────────────────────────────────
    const handleForgot = async (e) => {
        e.preventDefault();
        if (!slug) { setError('Enter your University Slug first.'); return; }
        setLoading(true); setError('');
        try {
            await api.post(`/tenant/${slug}/classroom/forgot-password`, { email: forgotEmail });
            setMode('sent');
        } catch (err) {
            setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
        } finally { setLoading(false); }
    };

    // ── Reset password ────────────────────────────────────────────────────────
    const handleReset = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
        if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return; }
        setLoading(true); setError('');
        try {
            await api.post(`/tenant/${slug}/classroom/reset-password`, { token: resetToken, new_password: newPassword });
            setMode('reset-done');
        } catch (err) {
            setError(err.response?.data?.detail || 'Reset failed. The link may have expired.');
        } finally { setLoading(false); }
    };

    const logoSrc = branding?.logoUrl || null;

    // ── Left panel (shared across modes) ─────────────────────────────────────
    const LeftPanel = (
        <Col md={6} className="bg-primary d-none d-md-flex flex-column justify-content-center p-5 text-white">
            <div className="mb-4 text-center">
                {logoSrc
                    ? <img src={logoSrc} alt={branding.institutionName} height="80" className="mb-3" onError={e => { e.target.style.display = 'none'; }} />
                    : <img src="/assets/images/logo.png" alt="AcademicOS" height="60" className="mb-3" onError={e => { e.target.style.display = 'none'; }} />
                }
                <h2 className="fw-bold">{branding?.institutionName || 'AcademicOS Classroom'}</h2>
                <p className="opacity-75">Grounded Intelligence for the Modern Educator.</p>
            </div>
            <div className="mt-5">
                <div className="d-flex align-items-start gap-3 mb-4">
                    <div className="bg-white bg-opacity-25 p-2 rounded"><BookOpen size={20} /></div>
                    <div><h6 className="mb-1">Instructional Copilot</h6><small className="opacity-75">Draft lectures and slides grounded in your textbooks.</small></div>
                </div>
                <div className="d-flex align-items-start gap-3">
                    <div className="bg-white bg-opacity-25 p-2 rounded"><ShieldCheck size={20} /></div>
                    <div><h6 className="mb-1">Institutional Integrity</h6><small className="opacity-75">Verified content mapped to your University curriculum.</small></div>
                </div>
            </div>
        </Col>
    );

    return (
        <Container fluid className="vh-100 d-flex align-items-center justify-content-center bg-light">
            <Row className="w-100 shadow-lg rounded-4 overflow-hidden" style={{ maxWidth: '1000px', minHeight: '600px' }}>
                {LeftPanel}

                <Col md={6} className="p-5 d-flex flex-column justify-content-center bg-white">

                    {/* ── LOGIN MODE ─────────────────────────────────────── */}
                    {mode === 'login' && (
                        <>
                            <div className="mb-4 text-center text-md-start">
                                <h3 className="fw-bold text-dark">Faculty Sign In</h3>
                                <p className="text-muted small">Access your grounded teaching workspace</p>
                            </div>
                            {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
                            <Form onSubmit={handleLogin}>
                                {[
                                    { label: 'University Slug',  type: 'text',     val: slug,     set: v => setSlug(v.toLowerCase().trim()), ph: 'e.g. tau-gy'           },
                                    { label: 'University Email', type: 'email',    val: email,    set: setEmail,    ph: 'professor@uni.edu' },
                                    { label: 'Password',        type: 'password',  val: password, set: setPassword, ph: '••••••••'           },
                                ].map(f => (
                                    <Form.Group className="mb-3" key={f.label}>
                                        <Form.Label className="x-small fw-bold text-muted text-uppercase">{f.label}</Form.Label>
                                        <Form.Control type={f.type} placeholder={f.ph} value={f.val} onChange={e => f.set(e.target.value)} required className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent" />
                                    </Form.Group>
                                ))}

                                <div className="d-flex justify-content-end mb-2">
                                    <button type="button" className="btn btn-link btn-sm p-0 text-muted text-decoration-none"
                                        onClick={() => { setError(''); setForgotEmail(email); setMode('forgot'); }}>
                                        Forgot password?
                                    </button>
                                </div>

                                <Form.Group className="mb-4">
                                    <Form.Check type="checkbox" id="classroom-terms-checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)}
                                        label={<span className="small text-muted">I agree to the <Link to="/terms" className="text-primary text-decoration-none fw-bold">Terms of Service</Link> and <Link to="/privacy" className="text-primary text-decoration-none fw-bold">Privacy Policy</Link>.</span>}
                                    />
                                </Form.Group>

                                <Button variant="primary" type="submit" className="w-100 fw-bold py-3 shadow mb-3" disabled={loading || !acceptedTerms}>
                                    {loading ? <Spinner animation="border" size="sm" /> : 'Enter Classroom'}
                                </Button>
                            </Form>

                            <div className="text-center mt-3 border-top pt-4">
                                <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                                    <UserPlus size={16} className="text-primary" />
                                    <span className="small text-muted">New to the Faculty Portal?</span>
                                </div>
                                <Button variant="outline-primary" size="sm" className="w-100 rounded-pill fw-bold"
                                    onClick={() => navigate(`/classroom-register${slug ? `?slug=${slug}` : ''}`)}>
                                    Request Institutional Access
                                </Button>
                                <p className="x-small text-muted mt-3 mb-0">Need help? <a href="mailto:support@tatvaone.ai" className="text-primary text-decoration-none fw-bold">Contact IT Dept</a></p>
                            </div>
                            <div className="mt-4 text-center">
                                <Badge bg="light" text="dark" className="border px-3 py-2 fw-normal rounded-pill">
                                    <GraduationCap size={14} className="me-2 text-primary" />
                                    {slug ? `Member of ${slug.toUpperCase()}` : 'Institutional Roster'}
                                </Badge>
                            </div>
                        </>
                    )}

                    {/* ── FORGOT PASSWORD MODE ───────────────────────────── */}
                    {mode === 'forgot' && (
                        <>
                            <button type="button" className="btn btn-link btn-sm p-0 mb-4 text-muted d-flex align-items-center gap-1"
                                onClick={() => { setError(''); setMode('login'); }}>
                                <ArrowLeft size={14} /> Back to sign in
                            </button>
                            <div className="mb-4">
                                <div className="p-3 bg-primary bg-opacity-10 rounded-circle d-inline-flex mb-3"><Mail size={24} className="text-primary" /></div>
                                <h4 className="fw-bold text-dark">Forgot your password?</h4>
                                <p className="text-muted small">Enter your email and we'll send a reset link valid for 10 minutes.</p>
                            </div>
                            {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
                            <Form onSubmit={handleForgot}>
                                {!slug && (
                                    <Form.Group className="mb-3">
                                        <Form.Label className="x-small fw-bold text-muted text-uppercase">University Slug</Form.Label>
                                        <Form.Control type="text" placeholder="e.g. tau-gy" value={slug} onChange={e => setSlug(e.target.value.toLowerCase().trim())} required className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent" />
                                    </Form.Group>
                                )}
                                <Form.Group className="mb-4">
                                    <Form.Label className="x-small fw-bold text-muted text-uppercase">University Email</Form.Label>
                                    <Form.Control type="email" placeholder="professor@uni.edu" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent" />
                                </Form.Group>
                                <Button variant="primary" type="submit" className="w-100 fw-bold py-3" disabled={loading}>
                                    {loading ? <Spinner animation="border" size="sm" /> : 'Send Reset Link'}
                                </Button>
                            </Form>
                        </>
                    )}

                    {/* ── SENT MODE ─────────────────────────────────────── */}
                    {mode === 'sent' && (
                        <div className="text-center">
                            <div style={{ fontSize: 56, marginBottom: 16 }}>📬</div>
                            <h4 className="fw-bold text-dark mb-2">Check your inbox</h4>
                            <p className="text-muted mb-4">If <b>{forgotEmail}</b> is registered, a password reset link has been sent. It expires in <b>10 minutes</b>.</p>
                            <Button variant="outline-primary" className="rounded-pill" onClick={() => { setError(''); setMode('login'); }}>
                                Back to Sign In
                            </Button>
                            <p className="text-muted x-small mt-3">Didn't receive it? Check your spam folder or <button type="button" className="btn btn-link btn-sm p-0 x-small" onClick={() => setMode('forgot')}>try again</button>.</p>
                        </div>
                    )}

                    {/* ── RESET PASSWORD MODE ────────────────────────────── */}
                    {mode === 'reset' && (
                        <>
                            <div className="mb-4">
                                <div className="p-3 bg-success bg-opacity-10 rounded-circle d-inline-flex mb-3"><KeyRound size={24} className="text-success" /></div>
                                <h4 className="fw-bold text-dark">Set a new password</h4>
                                <p className="text-muted small">Choose a strong password for your account.</p>
                            </div>
                            {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
                            <Form onSubmit={handleReset}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="x-small fw-bold text-muted text-uppercase">New Password</Form.Label>
                                    <Form.Control type="password" placeholder="Minimum 6 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)} required className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent" />
                                </Form.Group>
                                <Form.Group className="mb-4">
                                    <Form.Label className="x-small fw-bold text-muted text-uppercase">Confirm Password</Form.Label>
                                    <Form.Control type="password" placeholder="Re-enter password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent" />
                                </Form.Group>
                                <Button variant="success" type="submit" className="w-100 fw-bold py-3" disabled={loading}>
                                    {loading ? <Spinner animation="border" size="sm" /> : 'Reset Password'}
                                </Button>
                            </Form>
                        </>
                    )}

                    {/* ── RESET DONE MODE ───────────────────────────────── */}
                    {mode === 'reset-done' && (
                        <div className="text-center">
                            <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
                            <h4 className="fw-bold text-dark mb-2">Password reset!</h4>
                            <p className="text-muted mb-4">Your password has been updated. You can now sign in with your new password.</p>
                            <Button variant="primary" className="rounded-pill px-4" onClick={() => { setError(''); setMode('login'); }}>
                                Go to Sign In
                            </Button>
                        </div>
                    )}

                </Col>
            </Row>
        </Container>
    );
};

export default ClassroomLoginPage;
